import React from 'react';
import { View, StyleSheet, ViewStyle } from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

interface MaintenanceProgressBarProps {
  progress: number; // 1-3, representing the number of active segments
  variant?: 'desktop' | 'mobile';
  style?: ViewStyle;
}

/**
 * MaintenanceProgressBar - 3-segment progress indicator for maintenance tickets
 * Used across desktop and mobile views to show ticket status progression
 * 
 * @param progress - Number of active segments (1-3)
 * @param variant - 'desktop' or 'mobile' for styling differences
 * @param style - Additional container styles
 * 
 * @example
 * <MaintenanceProgressBar progress={2} variant="desktop" />
 */
export const MaintenanceProgressBar: React.FC<MaintenanceProgressBarProps> = ({
  progress,
  variant = 'desktop',
  style,
}) => {
  const containerStyle = variant === 'desktop' ? styles.desktopContainer : styles.mobileContainer;
  const segmentStyle = variant === 'desktop' ? styles.desktopSegment : styles.mobileSegment;

  return (
    <View style={[containerStyle, style]}>
      <View style={[segmentStyle, progress >= 1 && styles.segmentActive]} />
      <View style={[segmentStyle, progress >= 2 && styles.segmentActive]} />
      <View style={[segmentStyle, progress >= 3 && styles.segmentActive]} />
    </View>
  );
};

const styles = StyleSheet.create({
  // Desktop variant
  desktopContainer: {
    flexDirection: 'row',
    gap: 6,
  },
  desktopSegment: {
    width: 36,
    height: 8,
    borderRadius: 8,
    backgroundColor: PRIMITIVE_COLORS.white10,
  },

  // Mobile variant
  mobileContainer: {
    flexDirection: 'row',
    gap: 6,
  },
  mobileSegment: {
    width: 36,
    height: 8,
    borderRadius: 8,
    backgroundColor: PRIMITIVE_COLORS.white10,
  },

  // Active state (shared)
  segmentActive: {
    backgroundColor: PRIMITIVE_COLORS.successGreen,
  },
});

export default MaintenanceProgressBar;
